import * as ents from '@attain-apps/entities'

import type { Feature } from './feature'

/**
 * Strongly-typed selectors for the agent configuration feature.
 *
 * Per-key selectors are produced via `feature.createSelectorMethod`
 * which memoizes the underlying NgRx selector by key — calling
 * `selectors.agentDefaultValue(agentId)` repeatedly is cheap.
 */
export class Selectors {
  /** Resolves the persisted agent-default context (undefined if none). */
  public readonly agentDefaultValue
  /** True while the agent-default context is being loaded. */
  public readonly agentDefaultIsLoading
  /** True while the agent-default context is being saved. */
  public readonly agentDefaultIsSubmitting
  /** Last load/save error for the agent default, if any. */
  public readonly agentDefaultError
  /** Available knowledge-base files for the agent. */
  public readonly agentAvailableKnowledgeBaseFiles
  /** True when a knowledge-base file is selected in the active agent-default draft. */
  public readonly agentDraftFileIsSelected

  /** Resolves the persisted per-conversation override (undefined if none). */
  public readonly conversationOverrideValue
  /** True while the per-conversation override is being loaded. */
  public readonly conversationOverrideIsLoading
  /** True while the per-conversation override is being saved/deleted. */
  public readonly conversationOverrideIsSubmitting
  /** Last load/save error for the per-conversation override, if any. */
  public readonly conversationOverrideError

  /** Currently active editing session, if any. */
  public readonly editingSession
  /** True iff an editing session is open (drives modal visibility). */
  public readonly isEditing
  /**
   * Effective agent-default draft to display in the form.
   *
   * Falls back to the persisted value when the user hasn't touched
   * this scope yet, mirroring the previous `linkedSignal`-based
   * behavior but driven entirely from the store.
   */
  public readonly editingAgentDraft
  /**
   * Effective per-conversation draft to display in the form.
   *
   * `undefined` here means "inherit from agent default" — which is
   * exactly the signal the form needs to render its inherited banner.
   */
  public readonly editingConversationDraft
  /** True when a knowledge-base file is selected in the active conversation draft. */
  public readonly conversationDraftFileIsSelected

  // NOTE: types intentionally inferred so refactors flow through automatically
  constructor(feature: Feature) {
    const byAgent = feature.state.project(state => state.byAgent)
    const byConversation = feature.state.project(state => state.byConversation)
    const availableKnowledgeBaseFilesByAgent = feature.state.project(
      state => state.availableKnowledgeBaseFilesByAgent,
    )

    const agentEntry = feature.createSelectorMethod(
      (agentId: ents.core.agent.Agent.Id) =>
        byAgent.project(map => map[agentId]).asSelector,
    )
    const conversationEntry = feature.createSelectorMethod(
      (conversationId: ents.core.agent.Conversation.Id) =>
        byConversation.project(map => map[conversationId]).asSelector,
    )

    this.agentDefaultValue = feature.createSelectorMethod(
      (agentId: ents.core.agent.Agent.Id) =>
        agentEntry(agentId).project(entry => entry?.value).asSelector,
    )
    this.agentDefaultIsLoading = feature.createSelectorMethod(
      (agentId: ents.core.agent.Agent.Id) =>
        agentEntry(agentId).project(entry => entry?.status?.isLoading === true)
          .asSelector,
    )
    this.agentDefaultIsSubmitting = feature.createSelectorMethod(
      (agentId: ents.core.agent.Agent.Id) =>
        agentEntry(agentId).project(
          entry => entry?.status?.isSubmitting === true,
        ).asSelector,
    )
    this.agentDefaultError = feature.createSelectorMethod(
      (agentId: ents.core.agent.Agent.Id) =>
        agentEntry(agentId).project(entry => entry?.status?.error).asSelector,
    )
    this.agentAvailableKnowledgeBaseFiles = feature.createSelectorMethod(
      (agentId: ents.core.agent.Agent.Id) =>
        availableKnowledgeBaseFilesByAgent.project(map => map[agentId] ?? [])
          .asSelector,
    )

    this.conversationOverrideValue = feature.createSelectorMethod(
      (conversationId: ents.core.agent.Conversation.Id) =>
        conversationEntry(conversationId).project(entry => entry?.value)
          .asSelector,
    )
    this.conversationOverrideIsLoading = feature.createSelectorMethod(
      (conversationId: ents.core.agent.Conversation.Id) =>
        conversationEntry(conversationId).project(
          entry => entry?.status?.isLoading === true,
        ).asSelector,
    )
    this.conversationOverrideIsSubmitting = feature.createSelectorMethod(
      (conversationId: ents.core.agent.Conversation.Id) =>
        conversationEntry(conversationId).project(
          entry => entry?.status?.isSubmitting === true,
        ).asSelector,
    )
    this.conversationOverrideError = feature.createSelectorMethod(
      (conversationId: ents.core.agent.Conversation.Id) =>
        conversationEntry(conversationId).project(entry => entry?.status?.error)
          .asSelector,
    )

    const editing = feature.state.project(state => state.editing)

    this.editingSession = editing
    this.isEditing = editing.project(_editing => _editing != null)

    this.editingAgentDraft = editing.project(
      byAgent.asSelector,
      (_editing, _byAgent) => {
        if (!_editing) {
          return undefined
        }
        if (_editing.draftAgent) {
          return _editing.draftAgent.context
        }
        return _byAgent[_editing.agentId]?.value?.context
      },
    )
    this.agentDraftFileIsSelected = feature.createSelectorMethod(
      (file: ents.core.agent.KnowledgeBase.File) =>
        this.editingAgentDraft.project(draft =>
          knowledgeBaseFileIsSelected(draft, file),
        ).asSelector,
    )

    this.editingConversationDraft = editing.project(
      byConversation.asSelector,
      (_editing, _byConversation) => {
        if (_editing?.conversationId === undefined) {
          return undefined
        }
        if (_editing.draftConversation) {
          return _editing.draftConversation.context
        }
        return _byConversation[_editing.conversationId]?.value?.context
      },
    )
    this.conversationDraftFileIsSelected = feature.createSelectorMethod(
      (file: ents.core.agent.KnowledgeBase.File) =>
        this.editingConversationDraft.project(draft =>
          knowledgeBaseFileIsSelected(draft, file),
        ).asSelector,
    )
  }
}

function knowledgeBaseFileIsSelected(
  draft: ents.core.agent.Configuration.Context | undefined,
  file: ents.core.agent.KnowledgeBase.File,
): boolean {
  return (
    draft?.knowledgeBaseFiles?.some(selected =>
      ents.core.agent.KnowledgeBase.File.isEqual(selected, file),
    ) ?? false
  )
}
